import React, { useRef, useState, useEffect, useCallback } from 'react';
import Konva from 'konva';
import { FaSearchPlus, FaSearchMinus, FaCompress, FaExpand, FaSpinner, FaImage, FaMapMarkerAlt } from 'react-icons/fa';
import { denormalizePoints, getCentroid } from '../../InfoTerminal/utils/geometry';
import { getViewerOriginalUrl } from '../../../actions/infoTerminalViewer';
import styles from './styles.module.scss';

export default function SchemaCanvas({ objectData, allObjects, onAreaClick, highlightObjectId }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const layerRef = useRef(null);

  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
  const [imageObj, setImageObj] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [stageScale, setStageScale] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const [hoveredAreaId, setHoveredAreaId] = useState(null);
  const [stageInstance, setStageInstance] = useState(null);

  const isPanningRef = useRef(false);
  const lastPanPosRef = useRef({ x: 0, y: 0 });
  const stageScaleRef = useRef(1);
  const stagePosRef = useRef({ x: 0, y: 0 });

  useEffect(() => { stageScaleRef.current = stageScale; }, [stageScale]);
  useEffect(() => { stagePosRef.current = stagePosition; }, [stagePosition]);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (stageRef.current) {
      stageRef.current.destroy();
      stageRef.current = null;
      layerRef.current = null;
    }
    const stage = new Konva.Stage({
      container: canvasRef.current,
      width: containerSize.width,
      height: containerSize.height,
    });
    const layer = new Konva.Layer();
    stage.add(layer);
    stageRef.current = stage;
    layerRef.current = layer;
    setStageInstance(stage);

    return () => {
      if (stageRef.current) {
        stageRef.current.destroy();
        stageRef.current = null;
        layerRef.current = null;
        setStageInstance(null);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectData?.id, objectData?.has_image]);

  useEffect(() => {
    if (stageRef.current) {
      stageRef.current.width(containerSize.width);
      stageRef.current.height(containerSize.height);
    }
  }, [containerSize]);

  useEffect(() => {
    setImageObj(null);
    if (!objectData?.id || !objectData?.has_image) {
      setImageLoading(false);
      return;
    }
    setImageLoading(true);
    const img = new window.Image();
    img.src = getViewerOriginalUrl(objectData.id);
    img.onload = () => { setImageObj(img); setImageLoading(false); };
    img.onerror = () => setImageLoading(false);
  }, [objectData?.id, objectData?.has_image]);

  useEffect(() => {
    if (!imageObj || !containerSize.width || !containerSize.height) return;
    const pad = 20;
    const sc = Math.min(
      (containerSize.width - pad * 2) / imageObj.width,
      (containerSize.height - pad * 2) / imageObj.height,
      5
    );
    setStageScale(sc);
    setStagePosition({
      x: (containerSize.width - imageObj.width * sc) / 2,
      y: (containerSize.height - imageObj.height * sc) / 2,
    });
  }, [imageObj, objectData?.id]);

  useEffect(() => {
    if (!layerRef.current || !stageRef.current) return;
    const layer = layerRef.current;
    layer.destroyChildren();
    layer.scale({ x: stageScale, y: stageScale });
    layer.position(stagePosition);

    if (imageObj) {
      layer.add(new Konva.Image({ image: imageObj, x: 0, y: 0 }));
    }

    const areas = objectData?.areas || [];
    areas.forEach(area => {
      if (!imageObj) return;
      const pts = denormalizePoints(area.polygon, imageObj.width, imageObj.height);
      const flatPts = pts.flatMap(p => [p.x, p.y]);
      const isHovered = hoveredAreaId === area.id;
      const isSelected = area.target_object_id === highlightObjectId;
      const isActive = isHovered || isSelected;

      const poly = new Konva.Line({
        points: flatPts,
        closed: true,
        fill: isActive ? 'rgba(123, 43, 42, 0.35)' : 'rgba(100, 200, 100, 0.18)',
        stroke: isActive ? '#7b2b2a' : '#4caf50',
        strokeWidth: (isActive ? 2.5 : 1.5) / stageScale,
        hitStrokeWidth: 12 / stageScale,
      });

      poly.on('click', (e) => { e.cancelBubble = true; onAreaClick(area.target_object_id); });
      poly.on('mouseenter', () => setHoveredAreaId(area.id));
      poly.on('mouseleave', () => setHoveredAreaId(null));
      layer.add(poly);

      if (pts.length >= 3) {
        const linked = allObjects.find(o => o.id === area.target_object_id);
        if (linked?.name) {
          const c = getCentroid(pts);
          const text = new Konva.Text({
            x: c.x, y: c.y,
            text: linked.name,
            fontSize: 13 / stageScale,
            fontFamily: 'Inter, Arial, sans-serif',
            fill: isActive ? '#7b2b2a' : '#1a2535',
            fontStyle: 'bold',
            align: 'center',
            shadowColor: 'white',
            shadowBlur: 4,
            shadowOpacity: 0.9,
          });
          text.offsetX(text.width() / 2);
          text.offsetY(text.height() / 2);
          layer.add(text);
        }
      }
    });

    layer.batchDraw();
  }, [imageObj, objectData, stageScale, stagePosition, hoveredAreaId, highlightObjectId, allObjects, onAreaClick]);

  useEffect(() => {
    if (!stageInstance) return;
    const stage = stageInstance;

    const onWheel = (e) => {
      e.evt.preventDefault();
      const sc = stageScaleRef.current;
      const pos = stagePosRef.current;
      const ptr = stage.getPointerPosition();
      const by = 1.15;
      const ns = Math.min(Math.max(e.evt.deltaY > 0 ? sc / by : sc * by, 0.05), 10);
      const mp = { x: (ptr.x - pos.x) / sc, y: (ptr.y - pos.y) / sc };
      setStageScale(ns);
      setStagePosition({ x: ptr.x - mp.x * ns, y: ptr.y - mp.y * ns });
    };

    const onMouseDown = () => {
      isPanningRef.current = true;
      const ptr = stage.getPointerPosition();
      lastPanPosRef.current = { x: ptr.x, y: ptr.y };
      stage.container().style.cursor = 'grabbing';
    };

    const onMouseMove = () => {
      if (!isPanningRef.current) return;
      const ptr = stage.getPointerPosition();
      if (!ptr) return;
      const dx = ptr.x - lastPanPosRef.current.x;
      const dy = ptr.y - lastPanPosRef.current.y;
      lastPanPosRef.current = { x: ptr.x, y: ptr.y };
      setStagePosition(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    };

    const onMouseUp = () => {
      isPanningRef.current = false;
      stage.container().style.cursor = 'default';
    };

    stage.on('wheel', onWheel);
    stage.on('mousedown', onMouseDown);
    stage.on('mousemove', onMouseMove);
    stage.on('mouseup', onMouseUp);

    return () => {
      stage.off('wheel', onWheel);
      stage.off('mousedown', onMouseDown);
      stage.off('mousemove', onMouseMove);
      stage.off('mouseup', onMouseUp);
    };
  }, [stageInstance]);

  const fitToView = useCallback(() => {
    if (!imageObj || !containerSize.width || !containerSize.height) return;
    const pad = 20;
    const sc = Math.min(
      (containerSize.width - pad * 2) / imageObj.width,
      (containerSize.height - pad * 2) / imageObj.height,
      5
    );
    setStageScale(sc);
    setStagePosition({
      x: (containerSize.width - imageObj.width * sc) / 2,
      y: (containerSize.height - imageObj.height * sc) / 2,
    });
  }, [imageObj, containerSize]);

  const showZoom = objectData?.has_image && imageObj && !imageLoading;

  return (
    <div ref={containerRef} className={styles.schemaArea}>
      <div
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, display: objectData?.has_image ? 'block' : 'none' }}
      />

      {imageLoading && (
        <div className={styles.loadingScreen}>
          <FaSpinner className={styles.spinner} size={28} />
          <p>Загрузка схемы…</p>
        </div>
      )}

      {!imageLoading && !objectData && (
        <div className={styles.canvasEmpty}>
          <FaMapMarkerAlt size={48} />
          <p>Выберите объект в дереве слева</p>
        </div>
      )}

      {!imageLoading && objectData && !objectData.has_image && (
        <div className={styles.canvasEmpty}>
          <FaImage size={48} />
          <p>Схема не загружена</p>
        </div>
      )}

      {showZoom && (
        <div className={styles.zoomControls}>
          <button className={styles.zoomBtn} onClick={() => setStageScale(s => Math.min(s * 1.2, 10))} title="Приблизить">
            <FaSearchPlus size={14} />
          </button>
          <button className={styles.zoomBtn} onClick={() => setStageScale(s => Math.max(s / 1.2, 0.05))} title="Отдалить">
            <FaSearchMinus size={14} />
          </button>
          <button className={styles.zoomBtn} onClick={fitToView} title="Вписать">
            <FaCompress size={14} />
          </button>
          <button className={styles.zoomBtn} onClick={() => { setStageScale(1); setStagePosition({ x: 0, y: 0 }); }} title="Сбросить">
            <FaExpand size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
